import * as React from 'react'

import { useLocalStorageState } from '@/lib/use-local-storage-state'

type Theme = 'dark' | 'light' | 'system'
type ResolvedTheme = 'dark' | 'light'

export type { Theme, ResolvedTheme }

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
  highContrastStorageKey?: string
  disableTransitionOnChange?: boolean
}

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
  resolvedTheme: ResolvedTheme
  highContrast: boolean
  setHighContrast: (value: boolean) => void
}

const COLOR_SCHEME_QUERY = '(prefers-color-scheme: dark)'
const CONTRAST_QUERY = '(prefers-contrast: more)'
const THEME_VALUES: Theme[] = ['dark', 'light', 'system']

const ThemeProviderContext = React.createContext<
  ThemeProviderState | undefined
>(undefined)

const parseTheme = (raw: string): Theme | undefined =>
  THEME_VALUES.includes(raw as Theme) ? (raw as Theme) : undefined

const parseBool = (raw: string) =>
  raw === 'true' ? true : raw === 'false' ? false : undefined

function getSystemTheme(): ResolvedTheme {
  return window.matchMedia(COLOR_SCHEME_QUERY).matches ? 'dark' : 'light'
}

function getSystemHighContrast(): boolean {
  return window.matchMedia(CONTRAST_QUERY).matches
}

function disableTransitionsTemporarily() {
  const root = document.documentElement
  root.classList.add('disable-transitions')

  return () => {
    window.getComputedStyle(document.body)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        root.classList.remove('disable-transitions')
      })
    })
  }
}

function isEditableTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  return !!target.closest("input, textarea, select, [contenteditable='true']")
}

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'theme',
  highContrastStorageKey = 'high-contrast',
  disableTransitionOnChange = true,
}: ThemeProviderProps) {
  const [theme, setTheme] = useLocalStorageState<Theme>(
    storageKey,
    parseTheme,
    (v) => v,
    defaultTheme,
  )

  const [systemTheme, setSystemTheme] =
    React.useState<ResolvedTheme>(getSystemTheme)
  const resolvedTheme: ResolvedTheme = theme === 'system' ? systemTheme : theme

  // `null` until the user makes an explicit choice; until then `highContrast`
  // tracks the OS via the prefers-contrast effect below.
  const [explicitContrast, setExplicitContrast] = useLocalStorageState<
    boolean | null
  >(highContrastStorageKey, parseBool, String, null)
  const [systemContrast, setSystemContrast] = React.useState(
    getSystemHighContrast,
  )
  const highContrast = explicitContrast ?? systemContrast
  const setHighContrast = (value: boolean) => setExplicitContrast(value)

  React.useEffect(() => {
    const root = document.documentElement
    const restoreTransitions = disableTransitionOnChange
      ? disableTransitionsTemporarily()
      : null

    root.classList.remove('light', 'dark')
    root.classList.add(resolvedTheme)

    if (restoreTransitions) restoreTransitions()
  }, [resolvedTheme, disableTransitionOnChange])

  React.useEffect(() => {
    const mediaQuery = window.matchMedia(COLOR_SCHEME_QUERY)
    const handleChange = () =>
      setSystemTheme(mediaQuery.matches ? 'dark' : 'light')
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  React.useEffect(() => {
    document.documentElement.classList.toggle('high-contrast', highContrast)
  }, [highContrast])

  // Track the OS pref without persisting — `highContrast` ignores it once the
  // user has chosen explicitly. Writing here would corrupt that signal.
  React.useEffect(() => {
    const mediaQuery = window.matchMedia(CONTRAST_QUERY)
    const handleChange = () => setSystemContrast(mediaQuery.matches)
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  // Shift required so bare `d` doesn't collide with browser accelerators.
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat) return
      if (event.metaKey || event.ctrlKey || event.altKey) return
      if (!event.shiftKey) return
      if (isEditableTarget(event.target)) return
      if (event.key.toLowerCase() !== 'd') return

      const current = theme
      const next: Theme =
        current === 'light'
          ? 'dark'
          : current === 'dark'
            ? 'light'
            : resolvedTheme === 'dark'
              ? 'light'
              : 'dark'
      setTheme(next)
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [theme, resolvedTheme, setTheme])

  return (
    <ThemeProviderContext.Provider
      value={{ theme, setTheme, resolvedTheme, highContrast, setHighContrast }}
    >
      {children}
    </ThemeProviderContext.Provider>
  )
}

export function useTheme() {
  const context = React.useContext(ThemeProviderContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

/** Resolves `theme === 'system'` against the live OS preference. */
export function useResolvedTheme(): ResolvedTheme {
  return useTheme().resolvedTheme
}
